/**
 * Created by Daniel on 07/26/2021. Show task in kanban/grid
 * ------ maintenance history ------
 * 10/20/2021 Simon Zhao enabled delta-row-update mode and added refresh method different from the intialization.
 * 8/26/2022 Mitty Zhang mitty added the code for single click to expand row in onRowSelected
 */

import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, HostListener, ChangeDetectorRef, ElementRef } from '@angular/core';
import { catchError, debounceTime, take } from 'rxjs/operators';
import { of, Subject, Subscription } from 'rxjs';
import { AppState } from '../../../redux';
import { Store } from '@ngrx/store';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';

import { Stage, ProcessInstance, Task, TaskDefinition, TaskStatus, WorkflowKanbanTabType, WorkflowKanbanFilterType, WorkflowKanbanFilterStatus, ProcessInstanceStatus } from '../../../tamalelibs/models/workflow.model';
import { GetOneProcessDefinition, WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { workflowKanbanFilterSelector, workflowKanbanGridColumnStatusSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { PROJECT_ID_COLUMN_ID, WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { WorkflowTaskService } from '../../../tamalelibs/services/workflow-task.service';

import { SystemUser } from '../../../tamalelibs/models/user.model';
import { BriefConfig } from '../../../widgets/widgets.model';
import { KanbanCardAction } from '../kanban-card/kanban-card.model';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { SlideSheetActionTypes, SlideSheetEventTypes } from '../../slide-sheet/slide-sheet.model';
import { CustomGroupRowInnerComponentActions } from '../grid-group-row-inner-render/custom-group-row-inner-render.model';
import { TamCustomGridColumnFilterComponent } from '../../../widgets/ag-grid-column-filter/custom-grid-column-filter.component';
import { CustomGroupRowInnerRenderer } from '../grid-group-row-inner-render/custom-group-row-inner-renderer.component';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { WorkflowKanbanScreenStateService } from '../workflow-kanban-screen-state/workflow-kanban-screen-state.service';
import { ProcessDefinitionViewModel } from '../process-definition-view.model';
import { WHITE_CLASS_NAME } from '../../../widgets/click-outside/click-outside.directive';
import { GridUtility } from '../../admin-pages/grid-utility';

@Component({
    selector: 'tam-task-view',
    templateUrl: './task-view.component.html',
    styleUrls: ['./task-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskViewComponent implements OnInit, OnDestroy {

    @Input() processDefinition: ProcessDefinitionViewModel;
    @Input() active: boolean;

    actionContextMenuConfig: ContextMenuConfig;
    allStageList: Array<Stage>;
    allTaskDefinitionList: Array<TaskDefinition>;
    assignContextMenuConfig: ContextMenuConfig;
    assignToMe = false;
    contextMenuConfig: ContextMenuConfig;
    columnDefs: Array<ColDef>;
    currentUser: SystemUser;
    data: Array<Task>;
    filterStatus = WorkflowKanbanFilterStatus.IN_PROCESS;
    firstGroupIndex = 0;
    frameworkComponents: any;

    groupByStage: boolean;
    groupCount: number;
    gridOptions: GridOptions;
    groupedTaskList: Array<Array<Task>>;
    groupRowInnerRenderer;
    groupRowRendererParams;
    highPriority = false;
    isLoading = true;
    lastGroupIndex: number;
    oneGroupWidth = 332;
    overDue = false;
    processInstanceList: Array<ProcessInstance>;
    showGroupCount: number;
    sortedGroupList: Array<Stage | TaskDefinition>;
    sortedNodeIdArray: Array<string>;
    sortedStageList: Array<Stage>;
    sortedTaskDefinitionList: Array<TaskDefinition>;
    sortedTaskList: Array<Task> = [];
    statusContextMenuConfig: ContextMenuConfig;
    taskCardConfig: BriefConfig = new BriefConfig();
    viewType = 0; // 0 - kanban, 1 - grid

    get isGridView() {
        return this.viewType === 1;
    }
    get isKanbanView() {
        return this.viewType === 0;
    }

    private _calculatePageSubject$: Subject<any> = new Subject();
    private _columnApi: ColumnApi;
    private _endScrollHandle;
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi: GridApi;
    private _gridBodyViewport;
    private _groupedTaskList: Array<Array<Task>>;
    private _isTriggered = false;
    private _oldPositionForScroll = 0;
    private _rowSelectedIndex = null;
    private _taskValueChanged = false;

    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _element: ElementRef,
        private _kanbanStateService: WorkflowKanbanScreenStateService,
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _taskService: WorkflowTaskService,
        private _workflowService: WorkflowService,
    ) { }

    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        // for task grid
        this.gridOptions = {
            debug: false,
            suppressPropertyNamesCheck: true,
            defaultColDef: {
                filter: 'agSetColumnFilter',
                minWidth: 50,
                sortable: true,
                resizable: true,
                cellStyle: (params) => this._getGridCellStyle(params),
                menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
            },
            immutableColumns: false, // grid 21.1.0 new Properties
            enableBrowserTooltips: true,
            groupDisplayType: 'groupRows',
            rowSelection: 'single',
            sortingOrder: ['asc', 'desc', null],
            maintainColumnOrder: true,
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressMakeColumnVisibleAfterUnGroup: true,
            suppressRowGroupHidesColumns: true,
        };
        this.groupRowRendererParams = {
            innerRenderer: CustomGroupRowInnerRenderer,
            services: {
            },
            isSpecifyGroupName: true,
            action$: new Subject()
        };
        this.frameworkComponents = {
            customGridColumnFilter: TamCustomGridColumnFilterComponent, // override ag-grid date filter            
        };

        // filters
        const kanbanFilter = this._storeQuerier.queryBySelector(workflowKanbanFilterSelector);
        if (kanbanFilter.has(WorkflowKanbanTabType.TASK_BOARD_KANBAN)) {
            const currFilter = kanbanFilter.get(WorkflowKanbanTabType.TASK_BOARD_KANBAN);
            this.filterStatus = currFilter[WorkflowKanbanFilterType.SHOW_FILTER_STATUS];
            this.assignToMe = !!currFilter[WorkflowKanbanFilterType.ASSIGN_TO_ME];
            this.overDue = !!currFilter[WorkflowKanbanFilterType.OVER_DUE];
            this.highPriority = !!currFilter[WorkflowKanbanFilterType.HIGH_PRIORITY];
            this.viewType = currFilter[WorkflowKanbanFilterType.VIEW_TYPE] === 0 ? 0 : 1;
        }

        // subscriptions
        this._destroySubscriptions.push(
            this._calculatePageSubject$.pipe(
                debounceTime(500),
            ).subscribe(() => this.calculatePage()),

            this.taskCardConfig.feedbackSubject$.subscribe(action => this._taskCardActionHandler(action)),

            this._slideSheetService.slideSheetFeedbackSubject$.subscribe(res => {
                if (res.type === SlideSheetEventTypes.VALUE_CHANGE) {
                    this._taskValueChanged = res.payload.isValueChanged;
                } else if (res.type === SlideSheetEventTypes.CLICK_OUTSIDE_SLIDESHEET) {
                    if (this._taskValueChanged) {
                        const subscription = this._alertWindow.warn(
                            'You have unsaved changes',
                            ['Are you sure you want to discard the changes?'],
                            'Discard',
                            'Go Back',
                            'slideSheet',
                            true).subscribe((result: boolean) => {
                                if (result && !this._isTriggered) {
                                    this._taskValueChanged = false;
                                    this._closeSlideSheet();
                                }
                                subscription.unsubscribe();
                            });
                    }
                    this._isTriggered = false;
                } else if (res.type === SlideSheetEventTypes.SLIDE_CLOSING) {
                    if (res.payload && res.payload.isTriggerByNewProject) {
                        this._store.dispatch({
                            type: WorkflowActionTypes.PROCESS_INSTANCE_DELETE,
                            payload: {
                                instance: res.payload.instance,
                                showNotification: false
                            }
                        });
                    }
                }
            }),
            this._kanbanStateService.refreshSubject$.subscribe(r => {
                this._refresh();
            })
        );

        // init context menus
        this._initContextMenu();
        // change filter status
        this._changeFilterCheckedStatus(this.filterStatus);
        // init data including kanban and gri
        this._initData();
        // save the filter status to store
        this._setFilterToStore();
        this._changeDetectorRef.detectChanges();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    // calculate kanban page
    calculatePage() {
        const width = window.innerWidth - 64; // 64 is navigation menu width
        let canShowGroupCount = Math.floor(width / this.oneGroupWidth);
        if (canShowGroupCount === 0) {
            canShowGroupCount = 1;
        }
        if (canShowGroupCount >= this.groupCount) {
            this.showGroupCount = this.groupCount;
        } else {
            this.showGroupCount = canShowGroupCount;
        }
        this.lastGroupIndex = this.showGroupCount + this.firstGroupIndex;
    }

    // change assignee filter
    changeAssigneeFilter(event) {
        this.assignContextMenuConfig.show$.next(new ContextMenuShowAction(event));
        this._closeSlideSheet();
    }

    // change filter status
    changeStatusFilter(event) {
        this.statusContextMenuConfig.show$.next(new ContextMenuShowAction(event));
        this._closeSlideSheet();
        this._changeDetectorRef.detectChanges();
    }

    // filter task by user kanban/grid
    filterByAssignToMe(event) {
        this.assignToMe = !this.assignToMe;
        if (this.viewType === 0) {
            this._filterTaskForKanban(this.filterStatus);
        } else if (this.viewType === 1) {
            this._filterTaskForGrid(this.filterStatus);
        }
        this._setFilterToStore();
        this.assignContextMenuConfig.items.forEach(item => item.checked = !item.checked);
    }

    // filter by priority kanban/grid
    filterByHighPriority(event) {
        this.highPriority = !this.highPriority;
        if (this.viewType === 0) {
            this._filterTaskForKanban(this.filterStatus);
        } else if (this.viewType === 1) {
            this._filterTaskForGrid(this.filterStatus);
        }
        this._setFilterToStore();
    }

    // filter by over due kanban/grid
    filterByOverDue(event) {
        this.overDue = !this.overDue;
        if (this.viewType === 0) {
            this._filterTaskForKanban(this.filterStatus);
        } else if (this.viewType === 1) {
            this._filterTaskForGrid(this.filterStatus);
        }
        this._setFilterToStore();
    }

    // filter by task status kanban/grid
    filterByShowClosed(type: WorkflowKanbanFilterStatus) {
        if (this.viewType === 0) {
            this._filterTaskForKanban(type);
        } else if (this.viewType === 1) {
            this._filterTaskForGrid(type);
        }
        this._changeFilterCheckedStatus(type);
        this._setFilterToStore();
    }

    /**
     * the function of returning the unique id for each row
     *
     * @param {Task} item
     * @memberof TaskViewComponent
     */
    getRowNodeId = (item: Task) => item.id;

    // get workflow instance for the task
    getInstanceForTask(task: Task) {
        return this.processInstanceList.find(item => item.id === task.processInstance.id);
    }

    // get task count number
    getTaskCount(index: number): number {
        return this.groupedTaskList[index].length;
    }

    // If using grouping and groupDisplayType = 'groupRows', then the group row is not focusable. When navigating, the grouping row is skipped. So not use keydown and onCellKeyDown on the grid, use keydown listener
    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        // grid focus el may be body, cell, row, so if focus el is kendo dialog or input, the keyboard event will return.
        if (document.activeElement.nodeName === 'INPUT' || this._rowSelectedIndex === null) {
            return;
        }
        // 'keyCode' is deprecatedts, so use event.key
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            this._onCellKeyDown(event);
        } else if (event.key === 'Enter') {
            const row = this._gridApi.getDisplayedRowAtIndex(this._rowSelectedIndex);
            if (row && row.group) {
                row.setExpanded(!row.expanded);
            }
        }
    }

    // get the last page
    lastPage(event) {
        if (this.firstGroupIndex > 0) {
            this.firstGroupIndex--;
            this.lastGroupIndex--;
        }
        this._emptyClick(event);
    }

    // get the next page
    nextPage(event) {
        if (this.lastGroupIndex < this.groupCount) {
            this.firstGroupIndex++;
            this.lastGroupIndex++;
        }
        this._emptyClick(event);
    }

    // grid scroll
    onBodyScroll(e) {
        if (e.direction === 'horizontal') {
            clearTimeout(this._endScrollHandle);
            this._endScrollHandle = setTimeout(() => {
                this.groupRowRendererParams.action$.next({
                    type: CustomGroupRowInnerComponentActions.UPDATE_COLUMN_WIDTH_BY_SCROLL,
                    payload: e.left - this._oldPositionForScroll
                });
                this._oldPositionForScroll = e.left;
                this._changeDetectorRef.detectChanges();
            }, 250);
        }
    }

    // change view bewteen task kanban and grid
    onChangeView(event): void {
        if (this.isGridView) {
            this._setColumnAutoSizeByWidth();
        }
        this.actionContextMenuConfig.show$.next(new ContextMenuShowAction(event, ''));
        this._changeDetectorRef.detectChanges();
    }

    // grid column moved event
    onColumnMoved(event) {
        if (event.source === 'uiColumnDragged') {
            this._updateGroupRowByColumns(event.column);
            this._saveGridColumnStatus();
        }
    }

    // grid column pinned event
    onColumnPinned(event) {
        this._updateGroupRowByColumns(event.column);
    }

    // grid column resized event
    onColumnResized(event) {
        if (event.source === 'uiColumnDragged' && event.finished) {
            this._updateGroupRowByColumns(event.column);
            this._saveGridColumnStatus();
        }
    }

    // grid column row group changed event
    onColumnRowGroupChanged(event) {
        const columns = event.columns.map((item, i) => ({
            colId: item.colId,
            rowGroupIndex: i
        }));
        this.columnDefs.forEach((column) => {
            const index = columns.findIndex(item => item.colId === column.colId);
            if (index !== -1) {
                column.rowGroupIndex = columns[index].rowGroupIndex;
                column.comparator = (valueA, valueB, nodeA, nodeB) => GridUtility.customColumnSortComparator(nodeA, nodeB, column);
            } else {
                delete column.comparator;
            }
        });
        this._gridApi.setColumnDefs(this.columnDefs);
    }

    // grid column visible event
    onColumnVisible(event) {
        this._updateGroupRowByColumns(event.column);
        this._saveGridColumnStatus();
        this._setColumnAutoSizeByWidth();
    }

    // filter changed event
    onFilterChanged() {
        this.groupRowRendererParams.action$.next({
            type: CustomGroupRowInnerComponentActions.UPDATE_COLUMN_AGGFUNC_VALUE
        });
    }

    // ag-grid ready event
    onGridReady(params) {
        this._gridApi = params.api;
        this._columnApi = params.columnApi;

        // set column state.
        const kanbanGridColumnStatus = this._storeQuerier.queryBySelector(workflowKanbanGridColumnStatusSelector);
        if (kanbanGridColumnStatus && kanbanGridColumnStatus.has(WorkflowKanbanTabType.TASK_BOARD_KANBAN)) {
            this._columnApi.applyColumnState({ state: kanbanGridColumnStatus.get(WorkflowKanbanTabType.TASK_BOARD_KANBAN) });
        } else {
            this._setColumnAutoSizeByWidth();
        }
        this._changeDetectorRef.detectChanges();
    }

    // grid mouse down event
    onMousedown(event) {
        // grid filter input
        if (event.srcElement && event.srcElement.nodeName === 'INPUT') {
            return;
        }
    }

    // refresh button event
    onRefreshClicked() {
        this._store.dispatch(new GetOneProcessDefinition({ id: this.processDefinition.definition.id }));
    }

    // kanban resize event
    onResize(event) {
        this._calculatePageSubject$.next();
    }

    // grid row selected event
    onRowSelected(row) {
        if (row.node && typeof row.node.setExpanded === 'function') {
            row.node.expand = row.node.expand === undefined ? false : !row.node.expand;
            row.node.setExpanded(row.node.expand);
        }

        if (this._taskValueChanged) {
            const subscription = this._alertWindow.warn(
                'You have unsaved changes',
                ['Are you sure you want to discard the changes?'],
                'Discard',
                'Go Back',
                'slideSheet',
                true
            ).subscribe((result: boolean) => {
                if (result) {
                    this._showTaskBySlideSheet(row);
                } else {
                    // set the previous row selected if user click cancel
                    this._gridApi.forEachNode((node) => {
                        if (node.rowIndex === this._rowSelectedIndex) {
                            node.setSelected(true);
                        } else {
                            node.setSelected(false);
                        }
                    });
                }
                subscription.unsubscribe();
            });
        } else {
            if ((row.rowIndex !== this._rowSelectedIndex) || !this._slideSheetService.isBeingShown) {
                this._showTaskBySlideSheet(row);
            }
        }
    }


    onSelectTask(event, task: Task) {
        let subscription = null; // Initialize subscription to null
        if (this._taskValueChanged) {
            subscription = this._alertWindow.warn(
                'You have unsaved changes',
                ['Are you sure you want to discard the changes?'],
                'Discard',
                'Go Back',
                'slideSheet',
                true
            ).subscribe((result: boolean) => {
                if (result) {
                    this._isTriggered = true;
                    this._onSelectTaskForKanban(event, task);
                }
                if (subscription) {
                    subscription.unsubscribe(); // Check if subscription is not null before calling unsubscribe
                }
            });
        } else {
            this._onSelectTaskForKanban(event, task);
        }
    }


    // optimize ngfor of task kanban card
    trackByFn(index, item) {
        return item.id;
    }

    // change filter status
    private _changeFilterCheckedStatus(type: WorkflowKanbanFilterStatus): void {
        this.statusContextMenuConfig.items.forEach(item => {
            if (item.id === type) {
                item.checked = true;
                this.filterStatus = item.id;
            } else {
                item.checked = false;
            }
        });
    }

    // get data source for grid
    private _changeProcessDefForGrid(_isInit: boolean = true) {
        if (_isInit) {
            const classMap = new Map<string, string>();
            classMap.set(TaskDefinition.getStageColumnKey(), WHITE_CLASS_NAME);
            const columnDefs = this._workflowService.generateTaskGridColumnDef(this.sortedTaskDefinitionList, this.processDefinition.definition, classMap);
            columnDefs.forEach(item => {
                if (item.rowGroup) {
                    item.comparator = (valueA, valueB, nodeA, nodeB) => GridUtility.customColumnSortComparator(nodeA, nodeB, item);
                }
            });
            this.columnDefs = columnDefs;
            this.sortedStageList = [];
            this.groupByStage = this._workflowService.getSortedStageList(this.sortedStageList, this.sortedTaskDefinitionList);
        }

        this.sortedTaskList = [];
        this.processInstanceList.forEach(item => {
            item.task.forEach(task => {
                if (task.taskDefinition && ProcessInstance.isNotRejectedOngoingTask(item, task)) {
                    // 1. filter out gateways without task definition.
                    // 2. filter out ongoing tasks for rejected projects.
                    task.processInstance = item; // This is required because task need to display instance value, special requirement
                    this.sortedTaskList.push(task);
                }
            });
        });
        this.sortedTaskList = this._workflowService.sortTask(this.sortedTaskList, this.sortedNodeIdArray);
        this._filterTaskForGrid(this.filterStatus, _isInit);
        if (this.groupByStage) { // Group by stage
            this.sortedGroupList = [...this.sortedStageList];
        } else { // Group by task definition
            this.sortedGroupList = [...this.sortedTaskDefinitionList];
        }

        if (_isInit) {
            setTimeout(() => {
                this._setColumnAutoSizeByWidth();
            });
        }
    }

    // get data source for kanban
    private _changeProcessDefForKanban() {
        this.processInstanceList = this.processDefinition.definition.processInstance;
        this.sortedNodeIdArray = this._workflowService.getSortedNodeIdArrayByProcessDefinition(this.processDefinition.definition);
        this.sortedTaskDefinitionList = this._workflowService.getSortedTaskDefinitionListBySortedIds(this.sortedNodeIdArray, this.allTaskDefinitionList);
        this.sortedStageList = [];
        this.groupByStage = this._workflowService.getSortedStageList(this.sortedStageList, this.sortedTaskDefinitionList);
        this.sortedTaskList = [];
        this.processInstanceList.forEach(item => {
            item.task.forEach(task => {
                if (task.taskDefinition && ProcessInstance.isNotRejectedOngoingTask(item, task)) { // filter out gateway
                    task.processInstance = item;
                    this.sortedTaskList.push(task);
                }
                // sync the primary entity setting from instances.
                task.processInstance.primaryEntity = item.primaryEntity;
            });
        });
        this.sortedTaskList = this._workflowService.sortTask(this.sortedTaskList, this.sortedNodeIdArray);
        if (this.groupByStage) { // Group by stage
            this.sortedGroupList = [...this.sortedStageList];
        } else { // Group by task definition
            this.sortedGroupList = [...this.sortedTaskDefinitionList];
        }
        this.groupCount = this.sortedGroupList.length;
        this._groupedTaskList = this._workflowService.getGroupedTaskList(this.sortedGroupList, this.sortedTaskList, this.groupByStage);
        this._filterTaskForKanban(this.filterStatus);
        this.calculatePage();
    }

    // close slide sheet
    private _closeSlideSheet() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE,
            payload: ''
        });
    }

    // prevent default event
    private _emptyClick(event) {
        if (event.preventDefault) {
            event.preventDefault();
        }
        if (event.stopPropagation) {
            event.stopPropagation();
        }
    }

    // fitler task for grid
    private _filterTaskForGrid(type: WorkflowKanbanFilterStatus, _isInit: boolean = true) {
        // remove the status filter from ag-grid
        this._removeGridColumnFilter('status');
        switch (type) {
            case WorkflowKanbanFilterStatus.IN_PROCESS: // in progress
                this.data = this.sortedTaskList.filter(task => {
                    return task.status === TaskStatus.TODO || task.status === TaskStatus.IN_PROGRESS;
                });
                break;
            case WorkflowKanbanFilterStatus.CLOSED: // close
                this.data = this.sortedTaskList.filter(task => {
                    return task.status !== TaskStatus.TODO && task.status !== TaskStatus.IN_PROGRESS;
                });
                break;
            case WorkflowKanbanFilterStatus.ALL: // all
                this.data = this.sortedTaskList;
                break;
            default:
                break;
        }

        if (this.assignToMe) {
            this.data = this.data.filter(task => {
                return (task.owner && task.owner.id === this.currentUser.id)
                    || (!task.owner && task.assignee.findIndex(ass => ass.id === this.currentUser.id) > -1);
            });
        }

        if (_isInit) {
            this._rowSelectedIndex = null;
        }
        if (this._columnApi) {
            const columnState = this._columnApi.getColumnState();
            const sortModel = columnState
                .filter(column => column.sort)
                .map(column => ({ colId: column.colId, sort: column.sort }));

            if (this._gridApi && sortModel.length === 0) {
                // explicitly sort the task list by project id, as the immutableData is on for task grid, which gets the grid would not render rows in the order we serve through the property rowData as long as there's overlapped rows between new rowData and the previous rowData.
                const sort = [
                    {
                        colId: PROJECT_ID_COLUMN_ID,
                        sort: 'asc' as any
                    }
                ];
                this._columnApi.applyColumnState({ state: sort });
            }
        }
    }

    // filter task for kanban view
    private _filterTaskForKanban(type: WorkflowKanbanFilterStatus) {
        this.groupedTaskList = [];
        this._groupedTaskList.forEach((group, index) => {
            let oneGroup: Array<Task> = group;
            if (this.assignToMe) {
                oneGroup = oneGroup.filter(task => {
                    return (task.owner && task.owner.id === this.currentUser.id)
                        || (!task.owner && task.assignee.findIndex(ass => ass.id === this.currentUser.id) > -1);
                });
            }
            if (this.overDue) {
                // TODO: Will add the logic according to the requirement later  Added by Daniel on 07/29/2021
            }
            if (this.highPriority) {
                oneGroup = oneGroup.filter(task => {
                    return task.priority > 0;
                });
            }
            switch (type) {
                case WorkflowKanbanFilterStatus.IN_PROCESS: // in progress
                    oneGroup = oneGroup.filter(task => {
                        return task.status === TaskStatus.TODO || task.status === TaskStatus.IN_PROGRESS;
                    });
                    break;
                case WorkflowKanbanFilterStatus.CLOSED: // close
                    oneGroup = oneGroup.filter(task => {
                        return task.status !== TaskStatus.TODO && task.status !== TaskStatus.IN_PROGRESS;
                    });
                    break;
                case WorkflowKanbanFilterStatus.ALL: // all
                    break;
                default:
                    break;
            }
            this.groupedTaskList.push(oneGroup);
        });
    }

    // get grid cell style
    private _getGridCellStyle(params) {
        const cellStyleObj = {};
        if (params.node && params.columnApi) {
            const leftPinColumns = params.columnApi.getDisplayedLeftColumns();
            if (leftPinColumns.length > 0) {
                if (!params.node.group && params.column.left === 0 && params.node.level !== 0 && params.column.pinned === 'left') {
                    const paddingLeft = params.node.level * 30;
                    cellStyleObj['padding-left'] = `${paddingLeft}px`;
                }
            } else {
                if (!params.node.group && params.column.left === 0 && params.node.level !== 0) {
                    const paddingLeft = params.node.level * 30;
                    cellStyleObj['padding-left'] = `${paddingLeft}px`;
                }
            }
        }

        return cellStyleObj;
    }

    // init data for kanban and grid
    private _initData() {
        this.allTaskDefinitionList = [];
        this.allStageList = [];
        this.allTaskDefinitionList.push(...this.processDefinition.definition.taskDefinition);
        this.allTaskDefinitionList.forEach(item => {
            if (item.stage && this.allStageList.findIndex(stage => stage.id === item.stage.id) < 0) {
                this.allStageList.push(item.stage);
            }
        });
        this._changeProcessDefForKanban();
        this._changeProcessDefForGrid();
        this.isLoading = false;
        this._changeDetectorRef.detectChanges();
    }

    // init context menu
    private _initContextMenu() {
        this._initViewChangeContextMenu();
        this._initFilterContextMenu();
    }

    // init filter context menu
    private _initFilterContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.forceRefresh = true;
        this.contextMenuConfig.single = true;
        this.contextMenuConfig.stateful = false;
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onKanbanActionMenuSelect(event)));

        this.assignContextMenuConfig = new ContextMenuConfig();
        this.assignContextMenuConfig.forceRefresh = true;
        this.assignContextMenuConfig.single = true;
        this.assignContextMenuConfig.stateful = true;
        this.assignContextMenuConfig.items = [
            {
                id: 'Current User',
                text: 'Current User',
                checked: this.assignToMe,
            },
            {
                id: 'Everyone',
                text: 'Everyone',
                checked: !this.assignToMe,
            },
        ];
        this._destroySubscriptions.push(this.assignContextMenuConfig.events$.subscribe(event => this._onFilterMenuSelect(event)));

        this.statusContextMenuConfig = new ContextMenuConfig();
        this.statusContextMenuConfig.forceRefresh = true;
        this.statusContextMenuConfig.single = true;
        this.statusContextMenuConfig.stateful = true;
        this.statusContextMenuConfig.items = [
            {
                id: 'In Progress',
                text: 'In Progress',
                checked: true,
            },
            {
                id: 'Closed',
                text: 'Closed',
                checked: false,
            },
            {
                id: 'All',
                text: 'All',
                checked: false,
            },
        ];
        this._destroySubscriptions.push(this.statusContextMenuConfig.events$.subscribe(event => this._onFilterMenuSelect(event)));
    }

    // init change view context menu
    private _initViewChangeContextMenu() {
        this.actionContextMenuConfig = new ContextMenuConfig();
        this.actionContextMenuConfig.forceRefresh = true;
        this.actionContextMenuConfig.single = true;
        this.actionContextMenuConfig.showItemIcon = true;
        this.actionContextMenuConfig.stateful = true;
        this.actionContextMenuConfig.items = [
            { id: 0, text: 'Kanban View', icon: 'kanban', checked: this.viewType === 0 },
            { id: 1, text: 'Grid View', icon: 'grid', checked: this.viewType === 1 },
        ];
        this._destroySubscriptions.push(this.actionContextMenuConfig.events$.subscribe(event => this._onViewChangeMenuSelect(event)));
    }

    // grid cell key down
    private _onCellKeyDown(event) {
        const selectedRow = (rowIndex) => {
            const node = this._gridApi.getDisplayedRowAtIndex(rowIndex);
            if (node.group) {
                this._closeSlideSheet();
            }
            this.onRowSelected({ node, event, rowIndex: node.rowIndex, data: node.data });
            this._resetScrollHeight(node);
        };
        if (event.key === 'ArrowDown') {
            let nextIndex = this._rowSelectedIndex;
            const totalCount = this._gridApi.getDisplayedRowCount();
            if (++nextIndex < totalCount) {
                selectedRow(nextIndex);
            }
        } else if (event.key === 'ArrowUp') {
            let nextIndex = this._rowSelectedIndex;
            if (--nextIndex > -1) {
                selectedRow(nextIndex);
            }
        }
    }

    // for kanban action menu select
    private _onKanbanActionMenuSelect(action) {
        const type = +action.payload.id;
        const task: Task = action.payload.data.task;
        const instance: ProcessInstance = action.payload.data.instance;
        switch (type) {
            case 0:
                // Show detail
                this.onSelectTask(action.payload.event, task);
                break;
            default:
                break;
        }
    }

    // filter menu select
    private _onFilterMenuSelect(action) {
        const type = action.payload.id;
        switch (type) {
            case 'In Progress':
            case 'Closed':
            case 'All':
                this.filterByShowClosed(type);
                break;
            case 'Current User':
                if (!this.assignToMe) {
                    this.filterByAssignToMe(action.payload.event);
                }
                break;
            case 'Everyone':
                if (this.assignToMe) {
                    this.filterByAssignToMe(action.payload.event);
                }
                break;
            default:
                break;
        }
        this._changeDetectorRef.detectChanges();
    }

    // kanban select task
    private _onSelectTaskForKanban(event, task: Task): void {
        this._showSlideSheetForTask(event, task);
    }

    // gird select task
    private _onSelectTaskForGrid(event, task: Task) {
        this._showSlideSheetForTask(event, task);
    }

    private _showSlideSheetForTask(event, task: Task): void {
        this._taskValueChanged = false;
        // get the instance from server to get the latest values
        this._workflowService.getProcessInstanceById(task.processInstance.id).pipe(
            catchError(() => {
                return of(null);
            }),
            take(1),
        ).subscribe(res => {
            const instance = ProcessInstance.parse(res, null, null);
            ProcessInstance.filterOutGateways(instance);
            // get the udpated task from responese
            const matchedTasks = instance.task.filter(item => item.id === task.id);
            let currentTask = task;
            if (matchedTasks && matchedTasks.length > 0) {
                currentTask = matchedTasks[0];
                currentTask.processInstance = instance;
            }
            // show task detail using slide sheet
            this._slideSheetService.showSlideSheetForTask(event, currentTask, instance, this.processDefinition.definition, this._taskService.getTaskEditable(task, this.currentUser));
        });
    }

    // on view change menu select
    private _onViewChangeMenuSelect(action): void {
        const type = +action.payload.id;
        switch (type) {
            case 0:
            case 1:
                this.viewType = type;
                this.actionContextMenuConfig.items.forEach((item, index) => item.checked = this.viewType === index);
                this._setFilterToStore();
                this._setColumnAutoSizeByWidth();
                this.filterByShowClosed(this.filterStatus);
                this._changeDetectorRef.detectChanges();
                break;
            default:
                break;
        }
    }

    // reset grid scroll height
    private _resetScrollHeight(row) {
        const viewportRows = this._gridBodyViewport.clientHeight / row.rowHeight;
        const currentRow = row.rowIndex + 1;
        const scrollTopRows = currentRow + 1 - viewportRows; // currentRow must add a greater number, example 1 to kepp space for scroll for grid
        // need to reset grid body viewport scrollTop, because use groupDisplayType = 'groupRows', the group row is skipped when key down/up. when we no not want skipped the group row, we must re-calcute grid body viewport scrollTop
        this._gridBodyViewport.scrollTop = scrollTopRows * row.rowHeight;
    }

    private _refresh() {
        if (this.processDefinition.definition) {
            this._changeProcessDefForGrid(false);
            this._changeProcessDefForKanban();
            this._changeDetectorRef.detectChanges();
        }
    }

    // remove column filter
    private _removeGridColumnFilter(columnFilterName: string): void {
        // Get a reference to the filter instance
        if (this.gridOptions && this.gridOptions.api) {
            const filterInstance = this.gridOptions.api.getFilterInstance(columnFilterName);
            if (filterInstance) {
                filterInstance.setModel(null);
                this.gridOptions.api.onFilterChanged();
            }
        }
    }

    // save the grid column status to store
    private _saveGridColumnStatus() {
        const _gridColumns = this._columnApi.getColumnState();
        this._store.dispatch({
            type: WorkflowActionTypes.SET_WORKFLOW_KANBAN_GRID_COLUMN_STATUS,
            payload: {
                key: WorkflowKanbanTabType.TASK_BOARD_KANBAN,
                value: _gridColumns
            }
        });
    }

    // set columns auto size by the grid width and total columns width.
    private _setColumnAutoSizeByWidth(): void {
        // get grid view port
        this._gridBodyViewport = this._element.nativeElement.querySelector('.body-area');
        if (this._gridBodyViewport) {
            // get the width of the grid
            const gridWidth = this._gridBodyViewport.offsetWidth;
            if (gridWidth > 0) {
                if (this._gridApi && this._columnApi) {
                    this._workflowService.setGridAutoSizeByWidth(gridWidth, this._gridApi, this._columnApi);
                }
            }
        } else {
            // size columns to fit if _gridBodyViewport is null
            if (this._gridApi) {
                this._gridApi.sizeColumnsToFit();
            }
        }
    }

    // save filter to store
    private _setFilterToStore() {
        this._store.dispatch({
            type: WorkflowActionTypes.SET_WORKFLOW_KANBAN_FILTER,
            payload: {
                key: WorkflowKanbanTabType.TASK_BOARD_KANBAN,
                value: {
                    [WorkflowKanbanFilterType.SHOW_FILTER_STATUS]: this.filterStatus,
                    [WorkflowKanbanFilterType.ASSIGN_TO_ME]: this.assignToMe,
                    [WorkflowKanbanFilterType.OVER_DUE]: this.overDue,
                    [WorkflowKanbanFilterType.HIGH_PRIORITY]: this.highPriority,
                    [WorkflowKanbanFilterType.VIEW_TYPE]: this.viewType,
                }
            }
        });
    }

    // show task by slide sheet
    private _showTaskBySlideSheet(row): void {
        this._rowSelectedIndex = row.rowIndex;
        row.node.setSelected(true);
        if (row.data && !row.group) { // group row no data
            this._onSelectTaskForGrid(row.event, row.data);
        }
    }

    // task kanban actions
    private _taskCardActionHandler(action) {
        const task: Task = action.payload.task;
        if (action.type === KanbanCardAction.ON_MEAT_BALL) {
            this.contextMenuConfig.items = [
                { id: '0', text: 'Show Detail', disabled: false },
            ];
            this._changeDetectorRef.detectChanges();
            this.contextMenuConfig.show$.next(new ContextMenuShowAction(action.payload.event, { task: task, instance: action.payload.instance }));
            this._emptyClick(action.payload.event);
        }
    }

    // update group row by columns
    private _updateGroupRowByColumns(column?) {
        // set column visible, resize column width, pinned column, drag column, redraw rows for indented rows
        if (column && (column.left === 0 || column.oldLeft === 0 || !column.visible)) {
            this._gridApi.redrawRows();
        }

        this.groupRowRendererParams.action$.next({
            type: CustomGroupRowInnerComponentActions.UPDATE_COLUMN_WIDTH_BY_OPERATION
        });
        this._changeDetectorRef.detectChanges();
    }
}
